import { useQueryClient } from '@tanstack/react-query'
import { match } from 'ts-pattern'
import { useReconnectingSSEStream } from '../../hooks/use-reconnecting-sse-stream.ts'
import type { SSEStream } from '../../hooks/use-sse-stream.ts'
import {
  invalidateFreeformUploadSessions,
  isFreeformUploadSessionStalled,
  isFreeformUploadSessionUnfinished,
  useGetFreeformUploadSession,
} from '../../utils/api-client/api-hooks.ts'
import { type FreeformUploadEvent, FreeformUploadEventSchema } from '../../utils/api-client/sse-schemas.ts'
import { buildPath } from '../../utils/build-path.ts'

const isTerminalEvent = (event: FreeformUploadEvent) =>
  match(event.type)
    .with('done', 'error', () => true)
    .otherwise(() => false)

// The platform ids each event by its position in the session's events, so the last id is how far a
// connection got.
const positionOf = ({ lastEventId }: SSEStream<FreeformUploadEvent>) => {
  const position = Number(lastEventId)
  return Number.isInteger(position) ? position : 0
}

const FREEFORM_STREAM_GIVEN_UP_ERROR = new Error('The Findings Intake run stream kept closing before the run ended')

type UseFreeformUploadEventsOptions = {
  organizationId?: string
  sessionId: string | undefined
  onEvent?: (event: FreeformUploadEvent) => unknown
}

/**
 * A Findings Intake run's event stream. A connection can drop before the run's terminal event, closing
 * cleanly (the platform's stream deadline, a deploy) or failing; while the session still reads as
 * unfinished it reconnects, backing off, and resumes after the last event received. The session is re-read
 * on each drop, so a run the platform has since finished ends the stream.
 *
 * `events` holds only the current connection's events; `useFreeformRun` keeps the earlier ones.
 */
export const useFreeformUploadEvents = ({
  organizationId = 'default-organization',
  sessionId,
  onEvent,
}: UseFreeformUploadEventsOptions) => {
  const queryClient = useQueryClient()
  const { data: session } = useGetFreeformUploadSession({ organizationId, sessionId })
  return useReconnectingSSEStream({
    path:
      sessionId !== undefined
        ? buildPath('/api/v1/organizations/:organizationId/freeform-uploads/:sessionId/events', {
            organizationId,
            sessionId,
          })
        : undefined,
    schema: FreeformUploadEventSchema,
    isFinished: isTerminalEvent,
    onEvent,
    progressOf: positionOf,
    reconnectsOnError: true,
    isRecordFinished:
      session !== undefined && (!isFreeformUploadSessionUnfinished(session) || isFreeformUploadSessionStalled(session)),
    reread: () => invalidateFreeformUploadSessions(queryClient),
    givenUpError: FREEFORM_STREAM_GIVEN_UP_ERROR,
  })
}
